import React, { useState, useEffect } from 'react';
import { getPortfolioAll, createPortfolio, updatePortfolio, deletePortfolio, seedPortfolio, togglePortfolioHome } from '../api';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000';
const empty = {
  title:'', category:'WEB', icon:'💻', color:'#3498db',
  description:'', client:'', project_date:'', project_url:'',
  video_url:'', is_product:0, features:[], tech_stack:[],
  sort_order:0, is_active:1, image_url:'',
};

const Modal = ({ title, onClose, children }) => (
  <div className="admin-modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
    <div className="admin-modal" style={{ maxWidth: 720 }}>
      <div className="admin-modal-header">
        <h3>{title}</h3>
        <button className="admin-modal-close" onClick={onClose}>✕</button>
      </div>
      <div className="admin-modal-body">{children}</div>
    </div>
  </div>
);

const PortfolioAdmin = () => {
  const [items, setItems]           = useState([]);
  const [loading, setLoading]       = useState(true);
  const [typeFilter, setTypeFilter] = useState('ALL');   // 'ALL' | 'PRODUCT' | 'PROJECT'
  const [catFilter,  setCatFilter]  = useState('ALL');   // 'ALL' | 'WEB' | 'APP'
  const [modal, setModal]           = useState(null);
  const [form, setForm]             = useState(empty);
  const [imageFile, setImageFile]   = useState(null);
  const [imgPreview, setImgPreview] = useState('');
  const [iconFile,   setIconFile]   = useState(null);
  const [iconPreview, setIconPreview] = useState('');
  const [videoFile, setVideoFile]   = useState(null);      // kept for progress tracking
  const [videoPreview, setVideoPreview] = useState('');    // kept for compat
  const [videoList, setVideoList]   = useState([]);         // [{url, label, file?, preview?}]
  const [newVideoUrl, setNewVideoUrl] = useState('');
  const [newVideoLabel, setNewVideoLabel] = useState('');
  const [saving, setSaving]         = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [seeding, setSeeding]       = useState(false);
  const [seedMsg, setSeedMsg]       = useState('');
  const [msg, setMsg]               = useState('');
  const [newFeat, setNewFeat]       = useState('');
  const [newTech, setNewTech]       = useState('');

  const load = async () => {
    setLoading(true);
    const res = await getPortfolioAll();
    if (res.success) setItems(res.data);
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const openAdd = () => {
    setForm(empty); setImageFile(null); setImgPreview('');
    setIconFile(null); setIconPreview('');
    setVideoFile(null); setVideoPreview('');
    setVideoList([]); setNewVideoUrl(''); setNewVideoLabel('');
    setModal('add'); setMsg('');
  };
  const openEdit = (p) => {
    setForm({ ...p, features: p.features||[], tech_stack: p.tech_stack||[] });
    setImageFile(null);
    setIconFile(null);
    // If existing icon is an uploaded image, show it as preview
    const existingIcon = p.icon && p.icon.startsWith('/uploads/')
      ? `${API_BASE}${p.icon}` : '';
    setIconPreview(existingIcon);
    const existingImg = p.image_url
      ? (p.image_url.startsWith('http') ? p.image_url : `${API_BASE}${p.image_url}`)
      : '';
    setImgPreview(existingImg);
    setVideoFile(null);
    setVideoPreview(p.video_url || '');
    // Parse existing videos
    let existingVideos = [];
    if (p.video_urls && Array.isArray(p.video_urls) && p.video_urls.length > 0) {
      existingVideos = p.video_urls;
    } else if (p.video_url && p.video_url.trim()) {
      existingVideos = [{ url: p.video_url, label: 'Demo Video' }];
    }
    setVideoList(existingVideos);
    setNewVideoUrl(''); setNewVideoLabel('');
    setModal('edit'); setMsg('');
  };
  const closeModal = () => {
    setModal(null); setForm(empty);
    setImageFile(null); setImgPreview('');
    setIconFile(null); setIconPreview('');
    setVideoFile(null); setVideoPreview('');
    setVideoList([]); setNewVideoUrl(''); setNewVideoLabel('');
  };

  const handleIconChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setIconFile(file);
    setIconPreview(URL.createObjectURL(file));
    setForm(f => ({ ...f, icon: '' })); // clear emoji when image selected
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setImageFile(file);
    setImgPreview(URL.createObjectURL(file));
  };

  const handleVideoChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    // Warn if > 50MB (still allowed up to 100MB but let them know it'll take a while)
    if (file.size > 50 * 1024 * 1024) {
      setMsg(`⚠️ Large file (${(file.size / 1024 / 1024).toFixed(1)} MB) — upload may take a minute. Consider using a YouTube/Vimeo URL instead for faster results.`);
    } else {
      setMsg('');
    }
    setVideoFile(file);
    setVideoPreview(URL.createObjectURL(file));
    setForm(f => ({ ...f, video_url: '' }));
  };

  const handleVideoUrlChange = (e) => {
    setForm(f => ({ ...f, video_url: e.target.value }));
    // Clear file if typing a URL
    if (e.target.value) {
      setVideoFile(null);
      setVideoPreview('');
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    setMsg('');
    setUploadProgress(0);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => {
        if (k === 'features' || k === 'tech_stack') fd.append(k, JSON.stringify(v));
        else if (v !== null && v !== undefined) fd.append(k, v);
      });
      if (imageFile) fd.append('image', imageFile);
      if (iconFile)  fd.append('icon_img', iconFile);

      // Videos: separate existing (url-only) from new file uploads
      const existingUrls = videoList.filter(v => !v.file).map(v => ({ url: v.url, label: v.label || 'Demo Video' }));
      const fileEntries  = videoList.filter(v => !!v.file);
      fd.append('video_urls_json', JSON.stringify(existingUrls));
      fileEntries.forEach((v, i) => {
        fd.append(`video_${i}`, v.file);
        fd.append(`video_label_${i}`, v.label || `Video ${i+1}`);
      });

      const hasNewVids = fileEntries.length > 0;
      const onProgress = hasNewVids ? (pct) => setUploadProgress(pct) : null;

      const res = modal === 'add'
        ? await createPortfolio(fd, onProgress)
        : await updatePortfolio(form.id, fd, onProgress);

      if (res.success) {
        setUploadProgress(100);
        await load();
        closeModal();
      } else {
        setMsg(res.message || 'Error saving.');
      }
    } catch (err) {
      setMsg(err.message || 'Network error. Please try again.');
    }
    setSaving(false);
    setUploadProgress(0);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this project?')) return;
    await deletePortfolio(id);
    await load();
  };

  const handleToggleHome = async (id, currentVal) => {
    await togglePortfolioHome(id);
    // Update local state optimistically
    setItems(prev => prev.map(p => p.id === id ? { ...p, show_on_home: currentVal ? 0 : 1 } : p));
  };

  const handleSeed = async () => {
    if (!window.confirm('This will insert all 23 built-in products & projects into the database (existing records will be updated, not replaced). Continue?')) return;
    setSeeding(true); setSeedMsg('');
    const res = await seedPortfolio();
    setSeedMsg(res.success ? `✅ ${res.message}` : `❌ ${res.message}`);
    if (res.success) await load();
    setSeeding(false);
    setTimeout(() => setSeedMsg(''), 6000);
  };

  const addFeature = () => {
    if (!newFeat.trim()) return;
    setForm(f => ({ ...f, features: [...f.features, { text: newFeat.trim(), ok: true }] }));
    setNewFeat('');
  };
  const removeFeature = (i) => setForm(f => ({ ...f, features: f.features.filter((_, idx) => idx !== i) }));
  const toggleFeature = (i) => setForm(f => ({ ...f, features: f.features.map((fe, idx) => idx === i ? { ...fe, ok: !fe.ok } : fe) }));

  const addTech = () => {
    if (!newTech.trim()) return;
    setForm(f => ({ ...f, tech_stack: [...f.tech_stack, newTech.trim()] }));
    setNewTech('');
  };
  const removeTech = (i) => setForm(f => ({ ...f, tech_stack: f.tech_stack.filter((_, idx) => idx !== i) }));

  const isVideoUrl = (url) => url && (url.includes('youtube') || url.includes('vimeo') || url.startsWith('http'));

  // Filtered items
  const filteredItems = items.filter(p => {
    const typeOk = typeFilter === 'ALL' || (typeFilter === 'PRODUCT' ? p.is_product : !p.is_product);
    const catOk  = catFilter  === 'ALL' || p.category === catFilter;
    return typeOk && catOk;
  });

  return (
    <div>
      <div className="admin-section-header">
        <h2>Portfolio</h2>
        <div style={{ display:'flex', gap:10, alignItems:'center' }}>
          {seedMsg && (
            <span style={{ fontSize:'0.82rem', fontWeight:600, color: seedMsg.startsWith('✅') ? '#27ae60' : '#e74c3c' }}>
              {seedMsg}
            </span>
          )}
          <button className="btn-admin-outline" onClick={handleSeed} disabled={seeding} title="Import all built-in products & projects">
            {seeding ? 'Seeding...' : 'Load Default Data'}
          </button>
          <button className="btn-admin-primary" onClick={openAdd}>+ Add Project</button>
        </div>
      </div>

      {/* ── Filter Bar ── */}
      <div style={{ display:'flex', alignItems:'center', gap:12, marginBottom:16, flexWrap:'wrap' }}>
        {/* Type filter */}
        <div style={{ display:'flex', background:'#f0f2f5', borderRadius:8, padding:3, gap:2 }}>
          {[
            { key:'ALL',     label:'All' },
            { key:'PRODUCT', label:'Products' },
            { key:'PROJECT', label:'Projects' },
          ].map(f => (
            <button key={f.key} onClick={() => setTypeFilter(f.key)} style={{
              padding:'6px 16px', border:'none', borderRadius:6, cursor:'pointer', fontSize:'0.82rem', fontWeight:600,
              background: typeFilter === f.key ? 'white' : 'transparent',
              color:       typeFilter === f.key ? '#16165a' : '#6c757d',
              boxShadow:   typeFilter === f.key ? '0 1px 4px rgba(0,0,0,0.12)' : 'none',
              transition:  'all 0.15s ease',
            }}>
              {f.label}
              {f.key !== 'ALL' && (
                <span style={{ marginLeft:5, fontSize:'0.7rem', fontWeight:700, background: typeFilter===f.key?'#e8603a':'#dee2e6', color: typeFilter===f.key?'white':'#6c757d', padding:'0 5px', borderRadius:50 }}>
                  {f.key === 'PRODUCT' ? items.filter(p => p.is_product).length : items.filter(p => !p.is_product).length}
                </span>
              )}
            </button>
          ))}
        </div>

        <span style={{ color:'#dee2e6', fontSize:'1.2rem' }}>|</span>

        {/* Category filter */}
        <div style={{ display:'flex', background:'#f0f2f5', borderRadius:8, padding:3, gap:2 }}>
          {[
            { key:'ALL',  label:'All Categories' },
            { key:'WEB',  label:'Web' },
            { key:'APP',  label:'App' },
            { key:'BOTH', label:'Both' },
          ].map(f => (
            <button key={f.key} onClick={() => setCatFilter(f.key)} style={{
              padding:'6px 16px', border:'none', borderRadius:6, cursor:'pointer', fontSize:'0.82rem', fontWeight:600,
              background: catFilter === f.key ? 'white' : 'transparent',
              color:       catFilter === f.key ? '#16165a' : '#6c757d',
              boxShadow:   catFilter === f.key ? '0 1px 4px rgba(0,0,0,0.12)' : 'none',
              transition:  'all 0.15s ease',
            }}>
              {f.label}
            </button>
          ))}
        </div>

        {/* Count */}
        <span style={{ marginLeft:'auto', fontSize:'0.8rem', color:'#6c757d', fontWeight:500 }}>
          Showing {filteredItems.length} of {items.length} items
        </span>
      </div>

      {loading ? <p>Loading...</p> : (
        <div className="admin-table-wrap">
          <table className="admin-table">
            <thead>
              <tr>
                <th>#</th><th>Image</th><th>Title</th><th>Type</th>
                <th>Category</th><th>Video</th><th>Home</th><th>Status</th><th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredItems.map((p, i) => (
                <tr key={p.id}>
                  <td>{i + 1}</td>
                  <td>
                    {p.image_url
                      ? <img src={p.image_url.startsWith('http') ? p.image_url : `${API_BASE}${p.image_url}`} alt="" className="img-preview" onError={e => e.target.style.display='none'} />
                      : <div className="img-placeholder" style={{ fontSize: p.icon && p.icon.startsWith('/uploads/') ? undefined : '1.4rem', padding:0 }}>
                          {p.icon && p.icon.startsWith('/uploads/') ? (
                            <img src={`${API_BASE}${p.icon}`} alt="icon" style={{ width:'100%', height:'100%', objectFit:'cover', borderRadius:6 }} onError={e=>e.target.style.display='none'}/>
                          ) : (p.icon || '💻')}
                        </div>
                    }
                  </td>
                  <td style={{ fontWeight:600, maxWidth:180, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{p.title}</td>
                  <td>
                    <span className={`status-badge ${p.is_product ? 'status-active' : 'status-new'}`}>
                      {p.is_product ? 'Product' : 'Project'}
                    </span>
                  </td>
                  <td><span className="status-badge status-new">{p.category}</span></td>
                  <td style={{ fontSize:'0.78rem', color: p.video_url ? '#27ae60' : '#adb5bd' }}>
                    {p.video_url ? '▶ Yes' : '—'}
                  </td>
                  {/* Show on Home toggle — only relevant when video exists */}
                  <td>
                    {p.video_url ? (
                      <button
                        title={p.show_on_home ? 'Showing on home page — click to hide' : 'Not shown on home page — click to show'}
                        onClick={() => handleToggleHome(p.id, p.show_on_home)}
                        style={{
                          width: 42, height: 24, borderRadius: 12, border: 'none', cursor: 'pointer',
                          background: p.show_on_home ? '#16a34a' : '#d1d5db',
                          position: 'relative', transition: 'background 0.25s ease',
                          display: 'inline-flex', alignItems: 'center',
                        }}
                      >
                        <span style={{
                          width: 18, height: 18, borderRadius: '50%', background: 'white',
                          position: 'absolute', top: 3,
                          left: p.show_on_home ? 21 : 3,
                          transition: 'left 0.25s ease',
                          boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
                        }} />
                      </button>
                    ) : (
                      <span style={{ color:'#d1d5db', fontSize:'0.75rem' }}>—</span>
                    )}
                  </td>
                  <td>
                    <span className={`status-badge ${p.is_active ? 'status-active' : 'status-inactive'}`}>
                      {p.is_active ? 'Active' : 'Hidden'}
                    </span>
                  </td>
                  <td>
                    <div className="actions">
                      <button className="btn-admin-primary btn-admin-sm" onClick={() => openEdit(p)}>✏️ Edit</button>
                      <button className="btn-admin-primary btn-admin-sm btn-admin-danger" onClick={() => handleDelete(p.id)}>🗑</button>
                    </div>
                  </td>
                </tr>
              ))}
              {!filteredItems.length && (
                <tr><td colSpan={9} style={{ textAlign:'center', color:'#adb5bd', padding:'40px' }}>
                  {items.length === 0 ? 'No projects yet.' : 'No items match the selected filters.'}
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {modal && (
        <Modal title={modal === 'add' ? '+ Add New Project' : '✏️ Edit Project'} onClose={closeModal}>
          {msg && (
            <div style={{ background:'#fef2f2', color:'#991b1b', border:'1px solid #fecaca', padding:'10px 14px', borderRadius:'8px', marginBottom:'14px', fontSize:'0.85rem', display:'flex', alignItems:'center', gap:8 }}>
              <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
              {msg}
            </div>
          )}
          <form onSubmit={handleSave}>

            {/* ── STEP 1: Type + Category — prominent at top ── */}
            <div style={{ background:'#f8f9fa', borderRadius:12, padding:'16px 18px', marginBottom:18, border:'1.5px solid #e9ecef' }}>
              <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', flexWrap:'wrap', gap:16 }}>

                {/* Type: Product / Project */}
                <div>
                  <div style={{ fontSize:'0.72rem', fontWeight:700, color:'#6c757d', textTransform:'uppercase', letterSpacing:'0.08em', marginBottom:8 }}>
                    Type
                  </div>
                  <div style={{ display:'flex', gap:6 }}>
                    <button
                      type="button"
                      onClick={() => setForm(f => ({ ...f, is_product: 1 }))}
                      style={{
                        padding:'8px 20px', borderRadius:8, border:'2px solid', cursor:'pointer', fontSize:'0.85rem', fontWeight:700, transition:'all 0.15s',
                        background: form.is_product ? '#16165a' : 'white',
                        borderColor: form.is_product ? '#16165a' : '#dee2e6',
                        color: form.is_product ? 'white' : '#6c757d',
                      }}
                    >
                      <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2" style={{marginRight:5,verticalAlign:'middle'}}><rect x="2" y="3" width="20" height="14" rx="2"/><path d="M8 21h8M12 17v4"/></svg>
                      Product
                    </button>
                    <button
                      type="button"
                      onClick={() => setForm(f => ({ ...f, is_product: 0 }))}
                      style={{
                        padding:'8px 20px', borderRadius:8, border:'2px solid', cursor:'pointer', fontSize:'0.85rem', fontWeight:700, transition:'all 0.15s',
                        background: !form.is_product ? '#16165a' : 'white',
                        borderColor: !form.is_product ? '#16165a' : '#dee2e6',
                        color: !form.is_product ? 'white' : '#6c757d',
                      }}
                    >
                      <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2" style={{marginRight:5,verticalAlign:'middle'}}><path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"/></svg>
                      Project
                    </button>
                  </div>
                </div>

                {/* Category: Web / App / Both */}
                <div>
                  <div style={{ fontSize:'0.72rem', fontWeight:700, color:'#6c757d', textTransform:'uppercase', letterSpacing:'0.08em', marginBottom:8 }}>
                    Category
                  </div>
                  <div style={{ display:'flex', gap:6 }}>
                    {[
                      { value:'WEB',  label:'Web',  icon:<svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2" style={{marginRight:5,verticalAlign:'middle'}}><circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/></svg> },
                      { value:'APP',  label:'App',  icon:<svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2" style={{marginRight:5,verticalAlign:'middle'}}><rect x="5" y="2" width="14" height="20" rx="2"/><line x1="12" y1="18" x2="12.01" y2="18"/></svg> },
                      { value:'BOTH', label:'Both', icon:<svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2" style={{marginRight:5,verticalAlign:'middle'}}><rect x="2" y="3" width="12" height="9" rx="1"/><rect x="9" y="12" width="8" height="10" rx="1"/></svg> },
                    ].map(opt => (
                      <button
                        key={opt.value}
                        type="button"
                        onClick={() => setForm(f => ({ ...f, category: opt.value }))}
                        style={{
                          padding:'8px 16px', borderRadius:8, border:'2px solid', cursor:'pointer', fontSize:'0.85rem', fontWeight:700, transition:'all 0.15s',
                          background:   form.category === opt.value ? '#e8603a' : 'white',
                          borderColor:  form.category === opt.value ? '#e8603a' : '#dee2e6',
                          color:        form.category === opt.value ? 'white' : '#6c757d',
                          display:'flex', alignItems:'center',
                        }}
                      >
                        {opt.icon}{opt.label}
                      </button>
                    ))}
                  </div>
                  {form.category === 'BOTH' && (
                    <div style={{ marginTop:6, fontSize:'0.75rem', color:'#6c757d', background:'#f9f9f7', padding:'6px 10px', borderRadius:6, border:'1px solid #e4e4e0' }}>
                      This item will appear in both <strong>Web</strong> and <strong>App</strong> filters on the portfolio page.
                    </div>
                  )}
                </div>

                {/* Status indicator */}
                <div style={{ textAlign:'center' }}>
                  <div style={{ fontSize:'0.72rem', fontWeight:700, color:'#6c757d', textTransform:'uppercase', letterSpacing:'0.08em', marginBottom:8 }}>
                    Summary
                  </div>
                  <div style={{ display:'flex', gap:6, alignItems:'center', flexWrap:'wrap', justifyContent:'center' }}>
                    <span style={{ padding:'4px 12px', borderRadius:50, fontSize:'0.75rem', fontWeight:700, background: form.is_product ? 'rgba(22,22,90,0.1)' : 'rgba(59,130,246,0.1)', color: form.is_product ? '#16165a' : '#3b82f6' }}>
                      {form.is_product ? 'Product' : 'Project'}
                    </span>
                    <span style={{ padding:'4px 12px', borderRadius:50, fontSize:'0.75rem', fontWeight:700, background:'rgba(232,96,58,0.1)', color:'#e8603a' }}>
                      {form.category === 'BOTH' ? 'WEB + APP' : form.category}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Title */}
            <div className="admin-form-group">
              <label>Project Title *</label>
              <input value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} placeholder="Project name" required />
            </div>

            {/* Icon + Color + Sort */}
            <div className="admin-form-row">
              {/* Icon: upload image OR emoji */}
              <div className="admin-form-group">
                <label>Icon <span style={{ color:'#9ca3af', fontWeight:400, fontSize:'0.78rem' }}>(image or emoji)</span></label>
                <div style={{ display:'flex', alignItems:'center', gap:10 }}>
                  {/* Preview box — same size as the icon shown in portfolio */}
                  <div style={{
                    width: 52, height: 52, borderRadius: 12,
                    background: form.color ? `${form.color}22` : '#f0f2ff',
                    border: '1.5px dashed #dee2e6',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    flexShrink: 0, overflow: 'hidden', position: 'relative',
                    cursor: 'pointer',
                  }}
                    onClick={() => document.getElementById('icon-upload-input').click()}
                    title="Click to upload an icon image"
                  >
                    {iconPreview ? (
                      <img src={iconPreview} alt="icon" style={{ width:'100%', height:'100%', objectFit:'cover', display:'block' }} />
                    ) : form.icon ? (
                      <span style={{ fontSize:'1.6rem' }}>{form.icon}</span>
                    ) : (
                      <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="#9ca3af" strokeWidth="1.5"><rect x="3" y="3" width="18" height="18" rx="3"/><path d="M3 9l4-4 4 4 4-4 4 4"/><circle cx="8.5" cy="14.5" r="1.5" fill="#9ca3af"/></svg>
                    )}
                    {/* Upload hover overlay */}
                    <div style={{ position:'absolute', inset:0, background:'rgba(0,0,0,0.4)', display:'flex', alignItems:'center', justifyContent:'center', opacity:0, transition:'opacity 0.2s' }}
                      onMouseEnter={e=>e.currentTarget.style.opacity='1'}
                      onMouseLeave={e=>e.currentTarget.style.opacity='0'}
                    >
                      <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="white" strokeWidth="2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg>
                    </div>
                  </div>

                  <div style={{ flex:1, display:'flex', flexDirection:'column', gap:6 }}>
                    {/* Hidden file input */}
                    <input id="icon-upload-input" type="file" accept="image/*" onChange={handleIconChange} style={{ display:'none' }} />
                    {/* Emoji fallback input */}
                    <input
                      value={form.icon}
                      onChange={e => { setForm({ ...form, icon: e.target.value }); setIconFile(null); setIconPreview(''); }}
                      placeholder="💻 emoji (if no image)"
                      maxLength={5}
                      style={{ padding:'8px 12px', border:'1.5px solid #dee2e6', borderRadius:8, fontSize:'1rem', width:'100%', fontFamily:'inherit' }}
                    />
                    <div style={{ display:'flex', gap:6 }}>
                      <button type="button" onClick={() => document.getElementById('icon-upload-input').click()}
                        style={{ flex:1, padding:'5px 8px', border:'1.5px solid #dee2e6', background:'white', borderRadius:7, cursor:'pointer', fontSize:'0.75rem', fontWeight:600, color:'#6c757d', display:'flex', alignItems:'center', gap:4, justifyContent:'center' }}>
                        <svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg>
                        Upload Image
                      </button>
                      {(iconFile || iconPreview) && (
                        <button type="button" onClick={() => { setIconFile(null); setIconPreview(''); }}
                          style={{ padding:'5px 8px', border:'1.5px solid #fecaca', background:'#fef2f2', borderRadius:7, cursor:'pointer', fontSize:'0.75rem', fontWeight:600, color:'#dc2626' }}>
                          ✕ Remove
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
              <div className="admin-form-group">
                <label>Card Color</label>
                <div style={{ display:'flex', gap:8, alignItems:'center' }}>
                  <input type="color" value={form.color} onChange={e => setForm({ ...form, color: e.target.value })} style={{ width:48, height:38, padding:2, border:'1.5px solid #dee2e6', borderRadius:8 }} />
                  <input value={form.color} onChange={e => setForm({ ...form, color: e.target.value })} style={{ flex:1 }} placeholder="#3498db" />
                </div>
              </div>
              <div className="admin-form-group">
                <label>Sort Order</label>
                <input type="number" value={form.sort_order} onChange={e => setForm({ ...form, sort_order: +e.target.value })} />
              </div>
            </div>

            {/* Description */}
            <div className="admin-form-group">
              <label>Description</label>
              <textarea value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} rows={3} placeholder="Project description..." />
            </div>

            {/* Client + Date */}
            <div className="admin-form-row">
              <div className="admin-form-group">
                <label>Client Name</label>
                <input value={form.client} onChange={e => setForm({ ...form, client: e.target.value })} placeholder="Client Company" />
              </div>
              <div className="admin-form-group">
                <label>Project Date</label>
                <input value={form.project_date} onChange={e => setForm({ ...form, project_date: e.target.value })} placeholder="March 2024" />
              </div>
            </div>

            {/* Project URL */}
            <div className="admin-form-group">
              <label>Project / Live URL</label>
              <input value={form.project_url} onChange={e => setForm({ ...form, project_url: e.target.value })} placeholder="https://example.com" />
            </div>

            {/* ── MULTI-VIDEO SECTION ── */}
            <div className="admin-form-group" style={{ background:'#f8f9fa', borderRadius:10, padding:'16px', border:'1.5px solid #e9ecef' }}>
              <label style={{ fontWeight:700, color:'#1a1a5e', display:'flex', alignItems:'center', gap:8, marginBottom:4 }}>
                <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2"><polygon points="5 3 19 12 5 21 5 3"/></svg>
                Demo Videos
                <span style={{ marginLeft:'auto', background:'#16165a', color:'white', borderRadius:50, fontSize:'0.68rem', fontWeight:800, padding:'2px 8px' }}>{videoList.length}</span>
              </label>
              <p style={{ fontSize:'0.78rem', color:'#6c757d', margin:'0 0 14px' }}>
                Add multiple videos — upload files or paste YouTube/Vimeo URLs.
              </p>

              {/* Existing videos list */}
              {videoList.length > 0 && (
                <div style={{ display:'flex', flexDirection:'column', gap:8, marginBottom:14 }}>
                  {videoList.map((v, idx) => (
                    <div key={idx} style={{ display:'flex', alignItems:'center', gap:8, background:'white', borderRadius:8, padding:'8px 12px', border:'1px solid #e9ecef' }}>
                      {/* Type indicator */}
                      <div style={{ width:28, height:28, borderRadius:7, background:'rgba(22,22,90,0.08)', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
                        {v.file
                          ? <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="#16165a" strokeWidth="2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg>
                          : <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="#16165a" strokeWidth="2"><polygon points="5 3 19 12 5 21 5 3"/></svg>
                        }
                      </div>
                      {/* Label edit */}
                      <input
                        value={v.label || ''}
                        onChange={e => setVideoList(prev => prev.map((item, i) => i===idx ? {...item, label:e.target.value} : item))}
                        placeholder={`Video ${idx+1} label`}
                        style={{ flex:1, padding:'5px 10px', border:'1px solid #dee2e6', borderRadius:6, fontSize:'0.82rem', minWidth:0 }}
                      />
                      {/* URL/filename preview */}
                      <span style={{ fontSize:'0.72rem', color:'#9ca3af', maxWidth:160, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', flexShrink:0 }}>
                        {v.file ? v.file.name : (v.url || '').split('/').pop().substring(0,30)}
                      </span>
                      {/* Remove */}
                      <button type="button" onClick={() => setVideoList(prev => prev.filter((_,i) => i!==idx))}
                        style={{ background:'none', border:'none', cursor:'pointer', color:'#dc2626', padding:'2px', flexShrink:0 }}>
                        <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {/* Add new video */}
              <div style={{ background:'white', borderRadius:9, padding:'12px', border:'1.5px dashed #dee2e6' }}>
                <div style={{ fontSize:'0.78rem', fontWeight:700, color:'#374151', marginBottom:10 }}>Add Video</div>

                {/* Label for new video */}
                <input
                  value={newVideoLabel}
                  onChange={e => setNewVideoLabel(e.target.value)}
                  placeholder="Label (e.g. Dashboard Demo, Full Walkthrough)"
                  style={{ width:'100%', padding:'8px 12px', border:'1.5px solid #dee2e6', borderRadius:7, fontSize:'0.83rem', marginBottom:8, fontFamily:'inherit' }}
                />

                {/* Upload file */}
                <div style={{ display:'flex', gap:8, marginBottom:8 }}>
                  <label style={{ flex:1, display:'flex', alignItems:'center', gap:6, padding:'8px 12px', border:'1.5px dashed #dee2e6', borderRadius:7, cursor:'pointer', fontSize:'0.82rem', color:'#6c757d', background:'#f9f9f7' }}>
                    <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg>
                    Upload MP4/WebM (max 500MB)
                    <input type="file" accept="video/mp4,video/webm,video/ogg,video/quicktime" style={{ display:'none' }}
                      onChange={e => {
                        const file = e.target.files[0];
                        if (!file) return;
                        // Warn for very large files but don't block — backend supports up to 500MB
                        if (file.size > 200 * 1024 * 1024) {
                          setMsg(`⚠️ Large file (${(file.size/1024/1024).toFixed(0)} MB) — upload may take several minutes. Keep this window open.`);
                        } else {
                          setMsg('');
                        }
                        setVideoList(prev => [...prev, { url:'', label: newVideoLabel || `Video ${prev.length+1}`, file, preview: URL.createObjectURL(file) }]);
                        setNewVideoLabel('');
                        e.target.value = '';
                      }}
                    />
                  </label>
                </div>

                {/* OR URL */}
                <div style={{ display:'flex', gap:6 }}>
                  <input
                    value={newVideoUrl}
                    onChange={e => setNewVideoUrl(e.target.value)}
                    placeholder="Or paste YouTube / Vimeo / direct URL..."
                    style={{ flex:1, padding:'8px 12px', border:'1.5px solid #dee2e6', borderRadius:7, fontSize:'0.82rem', fontFamily:'inherit' }}
                    onKeyDown={e => {
                      if (e.key === 'Enter') { e.preventDefault();
                        if (newVideoUrl.trim()) {
                          setVideoList(prev => [...prev, { url: newVideoUrl.trim(), label: newVideoLabel || `Video ${prev.length+1}` }]);
                          setNewVideoUrl(''); setNewVideoLabel('');
                        }
                      }
                    }}
                  />
                  <button type="button"
                    style={{ padding:'8px 14px', background:'#16165a', color:'white', border:'none', borderRadius:7, fontSize:'0.82rem', fontWeight:700, cursor:'pointer', flexShrink:0 }}
                    onClick={() => {
                      if (newVideoUrl.trim()) {
                        setVideoList(prev => [...prev, { url: newVideoUrl.trim(), label: newVideoLabel || `Video ${prev.length+1}` }]);
                        setNewVideoUrl(''); setNewVideoLabel('');
                      }
                    }}
                  >+ Add URL</button>
                </div>
              </div>
            </div>

            {/* Is Product toggle */}
            {/* Image Upload */}
            <div className="admin-form-group">
              <label>Project Screenshot / Image</label>
              <input type="file" accept="image/*" onChange={handleImageChange} style={{ padding:'8px', border:'1.5px dashed #dee2e6', borderRadius:8, cursor:'pointer' }} />
              {imgPreview && (
                <img src={imgPreview} alt="preview" style={{ marginTop:10, width:'100%', maxHeight:180, objectFit:'cover', borderRadius:10, border:'1px solid #e9ecef' }} />
              )}
            </div>

            {/* Features */}
            <div className="admin-form-group">
              <label>Key Features</label>
              <div className="feature-list">
                {form.features.map((f, i) => (
                  <div key={i} className="feature-item">
                    <input type="checkbox" checked={f.ok} onChange={() => toggleFeature(i)} title="Check = included" />
                    <input
                      type="text" value={f.text}
                      onChange={e => setForm(fm => ({ ...fm, features: fm.features.map((fe, idx) => idx===i ? { ...fe, text: e.target.value } : fe) }))}
                    />
                    <button type="button" className="remove-btn" onClick={() => removeFeature(i)}>✕</button>
                  </div>
                ))}
              </div>
              <div style={{ display:'flex', gap:8 }}>
                <input
                  value={newFeat} onChange={e => setNewFeat(e.target.value)}
                  placeholder="Add feature..."
                  onKeyDown={e => e.key==='Enter' && (e.preventDefault(), addFeature())}
                  style={{ flex:1, padding:'8px 12px', border:'1.5px solid #dee2e6', borderRadius:8, fontSize:'0.85rem' }}
                />
                <button type="button" className="btn-admin-primary btn-admin-sm" onClick={addFeature}>+ Add</button>
              </div>
            </div>

            {/* Tech Stack */}
            <div className="admin-form-group">
              <label>Tech Stack</label>
              <div className="tag-list">
                {form.tech_stack.map((t, i) => (
                  <span key={i} className="tag-item">{t} <button type="button" onClick={() => removeTech(i)}>✕</button></span>
                ))}
              </div>
              <div style={{ display:'flex', gap:8 }}>
                <input
                  value={newTech} onChange={e => setNewTech(e.target.value)}
                  placeholder="React, Node.js..."
                  onKeyDown={e => e.key==='Enter' && (e.preventDefault(), addTech())}
                  style={{ flex:1, padding:'8px 12px', border:'1.5px solid #dee2e6', borderRadius:8, fontSize:'0.85rem' }}
                />
                <button type="button" className="btn-admin-primary btn-admin-sm" onClick={addTech}>+ Add</button>
              </div>
            </div>

            {/* Active toggle */}
            <div className="admin-form-group">
              <div className="admin-switch-row">
                <label className="admin-switch">
                  <input type="checkbox" checked={!!form.is_active} onChange={e => setForm({ ...form, is_active: e.target.checked ? 1 : 0 })} />
                  <span className="admin-slider"></span>
                </label>
                <span style={{ fontSize:'0.88rem', fontWeight:600 }}>Active (show on website)</span>
              </div>
            </div>

            <div style={{ marginTop:8 }}>
              {/* Upload progress bar — shown when uploading a video file */}
              {saving && videoList.some(v=>v.file) && (
                <div style={{ marginBottom:12 }}>
                  <div style={{ display:'flex', justifyContent:'space-between', fontSize:'0.8rem', fontWeight:600, color:'#1a1a5e', marginBottom:6 }}>
                    <span>⬆️ Uploading video...</span>
                    <span>{uploadProgress}%</span>
                  </div>
                  <div style={{ width:'100%', height:8, background:'#e9ecef', borderRadius:4, overflow:'hidden' }}>
                    <div style={{
                      height:'100%',
                      width: `${uploadProgress}%`,
                      background: uploadProgress === 100
                        ? 'linear-gradient(90deg,#27ae60,#2ecc71)'
                        : 'linear-gradient(90deg,#1a1a5e,#ff6b35)',
                      borderRadius:4,
                      transition:'width 0.3s ease',
                    }} />
                  </div>
                  {uploadProgress > 0 && uploadProgress < 100 && (
                    <p style={{ fontSize:'0.72rem', color:'#6c757d', marginTop:4 }}>
                      Please wait — do not close this window
                    </p>
                  )}
                </div>
              )}

              <div style={{ display:'flex', gap:10 }}>
                <button
                  type="submit"
                  className="btn-admin-primary"
                  disabled={saving}
                  style={{ flex:1, justifyContent:'center', padding:'12px' }}
                >
                  {saving
                    ? (videoList.some(v=>v.file)
                        ? `⬆️ Uploading... ${uploadProgress}%`
                        : '⏳ Saving...')
                    : '💾 Save Project'}
                </button>
                <button type="button" className="btn-admin-outline" onClick={closeModal} disabled={saving}>
                  Cancel
                </button>
              </div>
            </div>
          </form>
        </Modal>
      )}
    </div>
  );
};

export default PortfolioAdmin;
